import { useEffect, useRef, useState } from 'react'
import { ChipsWrapper, ChipsItem, cn } from '@wellesley/ui'
import { useForm, UseFormReturn } from 'react-hook-form'
import type { DefaultValues, Path } from 'react-hook-form'
import useOutsideClick from 'hooks/useOutsideClick'
import { UserAvatar } from 'user'
import CommonFormField from '../form/CommonFormField'
import useListHeight from './hooks/useListHeight'
import EmptyList from './EmptyList'
import DropdownList from './DropdownList'
import Wrapper from './Wrapper'
import { Mail, TriangleAlert, Loader2Icon } from 'lucide-react'
import { isUser } from './helpers'
import { ListItem } from './types'

type CmProps<TForm extends { search?: string } = { search: string }> = {
  selectedList: ListItem[]
  dropdownList: ListItem[]
  searchText: string
  placeholder?: string
  acceptEmail?: boolean
  setSearchText: (text: string) => void
  isLoading?: boolean
  autoFocus?: boolean
  isStaticList?: boolean
  listMaxHeight?: number
  listClassName?: string
  resetOnSelect?: boolean
  isUsersList?: boolean
  form?: UseFormReturn<TForm>
  onSelect: (list: ListItem[]) => void
}

const InputFilter = <TForm extends { search?: string } = { search: string }>({
  selectedList,
  dropdownList,
  onSelect,
  searchText,
  acceptEmail,
  setSearchText,
  isLoading,
  placeholder,
  resetOnSelect,
  listMaxHeight,
  listClassName,
  autoFocus = false,
  isStaticList = false,
  isUsersList = false,
  form: externalForm,
}: CmProps<TForm>) => {
  const handleSelect = (item: ListItem) => {
    const isItemSelected = selectedList.some((value) => {
      if (isUser(value) && isUser(item)) {
        return value.id === item.id
      }
      return value === item
    })

    const newList = isItemSelected
      ? selectedList.filter((value: ListItem) => {
          if (isUser(value) && isUser(item)) {
            return value.id !== item.id
          }
          return value !== item
        })
      : [...selectedList, item]

    onSelect(newList)
  }

  const innerForm = useForm<TForm>({
    defaultValues: {
      search: searchText ?? '',
    } as unknown as DefaultValues<TForm>,
  })

  const form = externalForm || innerForm

  const containerRef = useRef<HTMLDivElement>(null)
  const fieldRef = useRef<HTMLDivElement>(null)
  const [isFocused, setIsFocused] = useState(false)
  const [isListOpen, setIsListOpen] = useState(false)
  const [emailDropdownList, setEmailDropdownList] = useState<string[]>([])
  const list = emailDropdownList.length > 0 ? emailDropdownList : dropdownList

  useEffect(() => {
    if (!searchText && emailDropdownList.length > 0) {
      setEmailDropdownList([])
    }
  }, [searchText, emailDropdownList])

  const handleClose = () => {
    setIsListOpen(false)
  }

  useEffect(() => {
    if (isFocused) {
      if (searchText.includes('@')) {
        setIsListOpen(false)
      } else {
        list.length === 0 ? setIsListOpen(false) : setIsListOpen(true)
      }
    }
  }, [list, isFocused, searchText])

  useOutsideClick(containerRef, handleClose)

  const handleReset = () => {
    setSearchText('')
    form?.resetField('search' as Path<TForm>)
    setEmailDropdownList([])
    containerRef.current?.querySelector('input')?.focus()
  }

  const handleItemClick = (item: ListItem) => {
    handleSelect(item)
    if (resetOnSelect) {
      handleReset()
    }
  }

  const handleChange = (text: string) => {
    setSearchText(text)

    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(text)
    const shouldValidateEmail = acceptEmail && text.includes('@')

    if (shouldValidateEmail) {
      if (isValidEmail) {
        setEmailDropdownList([text])
        return
      } else {
        setEmailDropdownList([])
      }
    }
  }

  const listHeight = useListHeight({
    fieldRef,
    listLength: selectedList.length,
    listMaxHeight,
  })

  const isEmail = emailDropdownList.length > 0

  return (
    <div ref={containerRef} className="relative">
      <div
        ref={fieldRef}
        className={cn(
          'rounded-m outline-input relative max-h-[136px] w-full ring-offset-white placeholder:text-gray-500',
          'overflow-y-auto text-base text-gray-900 ease-in-out file:border-0 hover:outline-2 hover:outline-gray-400',
          'outline outline-1 transition-all disabled:cursor-not-allowed disabled:text-gray-300',
          'duration-200 file:bg-transparent file:text-sm file:font-medium',
          'placeholder:text-base placeholder:text-gray-400 focus-visible:bg-gray-50 focus-visible:text-gray-900 focus-visible:outline-2',
          'focus-visible:outline-blue-600 disabled:outline-gray-200',
          selectedList.length > 0 && 'p-xxs',
          isListOpen && 'outline-2 !outline-blue-600',
          form?.formState.errors.search &&
            'outline-2 outline-red-500 hover:outline-red-500',
        )}
      >
        <ChipsWrapper>
          {selectedList.map((item) => {
            const isUserItem = isUser(item)
            const isEmailItem = !isUserItem && item?.includes('@')

            const icon = isUserItem ? (
              <UserAvatar
                size={24}
                imgUrl={item.avatar?.files[0].uri || ''}
                name={item.displayName}
              />
            ) : isEmailItem ? (
              <Mail />
            ) : undefined

            return (
              <ChipsItem
                key={isUserItem ? item.id : item}
                icon={icon}
                text={isUserItem ? item.displayName : item}
                onRemove={() => handleItemClick(item)}
                className="p-xxxs"
              />
            )
          })}
        </ChipsWrapper>

        <Wrapper form={form} renderProvider={!externalForm}>
          <div data-testid="searchField" className="grow">
            <CommonFormField
              type="text"
              name={'search' as Path<TForm>}
              control={form.control}
              inputClassName="!px-xxs !static !border-none !bg-transparent !outline-none focus-within:ring-0 focus:outline-none focus:ring-0 focus-visible:ring-0"
              autoFocus={autoFocus}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                }
              }}
              onFieldChange={handleChange}
              placeholder={placeholder}
              formMessageClassName="hidden"
            />
          </div>
        </Wrapper>
      </div>
      {form?.formState.errors.search && (
        <p className="mb-xxl pt-xxs gap-xxxs flex items-center text-sm text-red-500">
          <TriangleAlert size={16} />
          {form.formState.errors.search.message?.toString()}
        </p>
      )}
      <div
        className={cn(
          { hidden: !isListOpen && !isStaticList },
          'no-scrollbar mt-m p-xxs absolute left-0 top-full z-[1] w-full overflow-y-auto rounded-2xl border border-gray-200 bg-white shadow-[0px_0px_12px_0px_var(--alpha-black-10)]',
          {
            static: isStaticList,
          },
          listClassName,
        )}
        style={{
          height: `${listHeight}px`,
        }}
      >
        {isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Loader2Icon className="animate-spin" data-testid="loader" />
          </div>
        ) : (
          <div>
            {list.length === 0 ? (
              <EmptyList isUsersList={isUsersList} />
            ) : (
              <DropdownList
                isEmail={isEmail}
                dropdownList={list}
                onItemClick={handleItemClick}
                selectedList={selectedList}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default InputFilter
